import { useState, useRef, useEffect } from 'react';
import globalPageStyle from '../css/globalPage.module.scss';
import styles from '../css/shareAndEarnPage.module.scss';
import tableStyles from '../css/table.module.scss';
import { Link } from 'react-router-dom';
import { formatStatus } from '../helpers/functions';
import { useClickAway } from 'react-use';
import Pagination from '../components/pagination';
import CustomSearchInput from '../components/customSearchInput';
import { createPortal } from 'react-dom';
import CustomDropdown from '../components/customDropdown';
import CustomCalendar from '../components/customCalendar';
import { ShareAndEarnObject } from '../helpers/objects/shareAndEarnObject';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { EnvPaths } from '../helpers/environment';
import { toast } from 'react-toastify';
import { convertToCsv, downloadCsv } from '../helpers/csv/shareAndEarnCsvHelper';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

interface ApiResponse {
  data: ShareAndEarnObject[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
  };
  filters: {
    status: string;
    createdDateFrom: string | null;
    createdDateTo: string | null;
    search: string;
  };
}

const ShareAndEarn = () => {
  const [data, setData] = useState<ShareAndEarnObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>('');
  const [dateValues, setDateValues] = useState<any[]>([null, null]);
  const [isActionOpen, setIsActionOpen] = useState<string | null>('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const { t } = useTranslation();
  const [status, setStatus] = useState<{ value: string; label: string }>({ value: 'all', label: t('shareAndEarn.labels.all') });
  const [filterStatusOpen, setFilterStatusOpen] = useState<boolean>(false);
  const filterStatusDropRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dateRangeRef = useRef(null);

  const { user } = useAuth();

  useEffect(() => {
    setStatus((prev) => ({ ...prev, label: t('shareAndEarn.labels.all') }));
  }, [t, i18next]);

  const fetchData = async () => {
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        ...(searchInput && { q: searchInput }),
        ...(status.value !== 'all' && { status: status.value }),
        ...(dateValues[0] && { createdDateFrom: formatDateForBackend(dateValues[0]) }),
        ...(dateValues[1] && { createdDateTo: formatDateForBackend(dateValues[1]) }),
      });
      const response = await axios.get<ApiResponse>(`${EnvPaths.SERVER_URL}/admin/share-and-earn?${params}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      return response.data;
    } catch (err) {
      console.error(error);
      setError(t('shareAndEarn.error'));
      return null;
    }
  };

  useEffect(() => {
    if (!user) {
      return;
    }
    setLoading(true);
    const handler = setTimeout(() => {
      fetchData().then((response) => {
        setData(response?.data ?? []);
        setTotalPages(response?.pagination.totalPages ?? 0);
        setTotalRecords(response?.pagination.totalRecords ?? 0);
        setCurrentPage(response?.pagination.currentPage ?? 0);
        setLoading(false);
      });
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [user, searchInput, currentPage, status, dateValues]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleExportToCsv = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: 0,
        limit: 'all',
        ...(searchInput && { q: searchInput }),
        ...(status.value !== 'all' && { status: status.value }),
        ...(dateValues[0] && { createdDateFrom: formatDateForBackend(dateValues[0]) }),
        ...(dateValues[1] && { createdDateTo: formatDateForBackend(dateValues[1]) }),
      });
      const response = await axios.get<ApiResponse>(`${EnvPaths.SERVER_URL}/admin/share-and-earn?${params}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const responseData = response.data.data;

      const csvContent = convertToCsv(responseData, t);
      downloadCsv(csvContent);
    } catch (err) {
      console.error(error);
      setError(t('shareAndEarn.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateLink = async (id: string) => {
    try {
      await axios
        .put(
          `${EnvPaths.SERVER_URL}/admin/share-and-earn/${id}/deactivate`,
          {},
          {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          }
        )
        .then((response) => {
          setIsActionOpen('');
          toast.success(response.data.message);
          setData((prev) =>
            prev.map((item) =>
              item._id === id
                ? {
                    ...item,
                    status: 'deactivated',
                  }
                : item
            )
          );
        });
    } catch (error: unknown) {
      setIsActionOpen('');
      console.error(error);

      let message = t('shareAndEarn.error');
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      toast.error(message);
    }
  };

  const handleActionClick = (id: string, buttonElement: HTMLDivElement) => {
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 205 + window.scrollX,
      });
    }
    setIsActionOpen(isActionOpen === id ? null : id);
  };

  useClickAway(filterStatusDropRef, () => {
    setFilterStatusOpen(false);
  });

  const filterStatusOptions = [
    { value: 'all', label: t('shareAndEarn.labels.all') },
    { value: 'active', label: t('shareAndEarn.labels.active') },
    { value: 'deactivated', label: t('shareAndEarn.labels.deactivated') },
  ];

  const handleStatusChange = (item: { value: string; label: string }) => {
    setStatus(item);
    setFilterStatusOpen(false);
    setCurrentPage(1);
  };

  const onPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const onNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleDateChange = (value: any) => {
    setDateValues(value);
    setCurrentPage(1);
  };

  const handleDateOnCancel = () => {
    setDateValues([null, null]);
    setCurrentPage(1);
  };

  const formatDateForDisplay = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateForBackend = (date: any) => {
    if (date && date.toDate) {
      return date?.toDate()?.toISOString()?.split('T')[0];
    }
    return '';
  };

  return (
    <main className={globalPageStyle.pageContainer}>
      <div className={globalPageStyle.pageContent}>
        <div className={globalPageStyle.pageFlexContainer}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={styles.flexRow}>
                <CustomSearchInput type="text" value={searchInput} onChange={handleSearchChange} icon="/assets/images/search-icon.svg" style={{ cursor: 'text' }} placeholder={t('shareAndEarn.search')} />
                {/*date range - calendar*/}
                <CustomCalendar dateValues={dateValues} handleDateChange={handleDateChange} onCancel={handleDateOnCancel} ref={dateRangeRef} />
                {/*date range - calendar*/}
                {/*filter*/}
                <CustomDropdown ref={filterStatusDropRef} isOpen={filterStatusOpen} onToggle={() => setFilterStatusOpen(!filterStatusOpen)} options={filterStatusOptions} value={status} onChange={handleStatusChange} width="195px" label={t('shareAndEarn.status')} />
                {/*filter*/}
              </div>
            </div>
            <button type="button" className="primaryButton inverse" onClick={handleExportToCsv}>
              {t('shareAndEarn.exportCsv')}{' '}
            </button>
          </div>
          <div className={styles.body}>
            {/*Table*/}
            <div className={tableStyles.tableContainer}>
              <table className={`${tableStyles.table}`}>
                <thead className={tableStyles.tableHeader}>
                  <tr>
                    <th>{t('shareAndEarn.name')}</th>
                    <th>{t('shareAndEarn.email')}</th>
                    <th>{t('shareAndEarn.date')}</th>
                    <th>{t('shareAndEarn.status')}</th>
                    <th className={tableStyles.actionColumn}></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <div className="loader" />
                  ) : data.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={tableStyles.noData}>
                        {t('shareAndEarn.noData')}
                      </td>
                    </tr>
                  ) : (
                    data.map((shareAndEarn) => (
                      <tr key={shareAndEarn._id}>
                        <td>{`${shareAndEarn.user.lastName && (shareAndEarn.user.lastName.length > 25 ? shareAndEarn.user.lastName?.slice(0, 25) + '...' : shareAndEarn.user.lastName)} ${shareAndEarn.user.firstName && (shareAndEarn.user.firstName.length > 25 ? shareAndEarn.user.firstName?.slice(0, 25) + '...' : shareAndEarn.user.firstName)}`}</td>
                        <td>{shareAndEarn.user.email}</td>
                        <td>{formatDateForDisplay(shareAndEarn.createdAt)}</td>
                        <td>{formatStatus(t, shareAndEarn.status)}</td>
                        <td className={tableStyles.actionColumn}>
                          <div className={tableStyles.actionShape} ref={buttonRef}>
                            <img
                              src="/assets/images/dots.svg"
                              alt="dots"
                              onClick={(e) => {
                                const buttonElement = e.currentTarget as HTMLDivElement;
                                handleActionClick(shareAndEarn._id, buttonElement);
                              }}
                            />
                            {isActionOpen === shareAndEarn._id &&
                              typeof window !== 'undefined' &&
                              createPortal(
                                <div
                                  className={`${tableStyles.actionShapeDropdown}`}
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    left: `${dropdownPosition.left}px`,
                                  }}
                                >
                                  <div>
                                    <Link to={`/dashboard/shareandearn/referral/${shareAndEarn._id}`} className={tableStyles.actionShapeDropdownItem}>
                                      {t('shareAndEarn.viewUserDetails')}
                                    </Link>
                                  </div>
                                  <div className={tableStyles.actionShapeDropdownItem} onClick={() => handleDeactivateLink(shareAndEarn?._id)}>
                                    {t('shareAndEarn.deactivateLink')}
                                  </div>
                                </div>,
                                document.body
                              )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/*Table*/}
            {/*Pagination*/}
            <Pagination onPrevious={onPrevious} onNext={onNext} currentPage={currentPage} totalPages={totalPages} />
            {/*Pagination*/}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ShareAndEarn;
